import { Box, Tabs, Typography } from "@mui/material";
import styled, { keyframes } from "styled-components";

/* ---------- animations ---------- */

const pulseRing = keyframes`
  0%   { transform: scale(0.6); opacity: 0.9; }
  80%  { transform: scale(2.2); opacity: 0;   }
  100% { transform: scale(2.2); opacity: 0;   }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0;  }
`;

const underlineSlide = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

/* ---------- header status pill ---------- */

export const StatusPill = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  transition: border-color 0.18s ease, transform 0.18s ease;
  user-select: none;

  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.palette.borderColor.default : theme.palette.text.primary};
    transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(-1px)")};
  }
`;

export const PulseDot = styled(Box)`
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme, $active }) =>
    $active ? "#22c55e" : theme.palette.text.disabled};
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: ${({ $active }) =>
      $active ? "#22c55e" : "transparent"};
    animation: ${({ $active }) => ($active ? pulseRing : "none")} 1.8s ease-out infinite;
  }
`;

export const StatusText = styled(Typography).attrs({ variant: "caption" })`
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.palette.text.primary};
`;

/* ---------- tabs ---------- */

export const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  min-height: 42px;

  .MuiTabs-flexContainer {
    gap: 4px;
  }
  .MuiTab-root {
    text-transform: none;
    font-weight: 600;
    font-size: 0.875rem;
    min-height: 42px;
    padding: 8px 16px;
    color: ${({ theme }) => theme.palette.text.secondary};
    transition: color 0.18s ease;
    &:hover { color: ${({ theme }) => theme.palette.text.primary}; }
    &.Mui-selected { color: ${({ theme }) => theme.palette.text.primary}; }
  }
  .MuiTabs-indicator {
    height: 2px;
    background-color: ${({ theme }) => theme.palette.text.primary};
    transform-origin: left center;
    animation: ${underlineSlide} 0.28s ease-out;
  }
`;

/* ---------- shared cell bits ---------- */

export const SourceCell = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const Favicon = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
  object-fit: contain;
  background-color: ${({ theme }) => theme.palette.background.secondary};
`;

export const ActionRow = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/* ---------- empty state ---------- */

export const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 14px;
  padding: 64px 16px;
`;

export const RadarOrb = styled(Box)`
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  display: flex;
  align-items: center;
  justify-content: center;

  &::before, &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.palette.borderColor.dark};
    animation: ${pulseRing} 2.4s ease-out infinite;
  }
  &::after { animation-delay: 1.2s; }
`;

/* ---------- skeleton ---------- */

export const ShimmerBar = styled(Box)`
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.background.secondary} 0%,
    ${({ theme }) => theme.palette.background.dark} 50%,
    ${({ theme }) => theme.palette.background.secondary} 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s linear infinite;
`;

export const SkeletonRow = styled(Box)`
  display: grid;
  grid-template-columns: 1.6fr 1.6fr 0.4fr 0.5fr 0.5fr 0.6fr;
  gap: 16px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;

export const SkeletonHeader = styled(Box)`
  display: grid;
  grid-template-columns: 1.6fr 1.6fr 0.4fr 0.5fr 0.5fr 0.6fr;
  gap: 16px;
  padding: 14px 16px;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;

/* ---------- settings cards ---------- */

export const SettingsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-block-start: 16px;
`;

export const SettingsCard = styled(Box)`
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  border-radius: 12px;
  padding: 20px;
  background-color: ${({ theme }) => theme.palette.background.default};
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 100%;
`;

export const SettingsCardWide = styled(SettingsCard)`
  grid-column: 1 / -1;
`;

export const CardHeading = styled(Typography)`
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const StatGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
`;

export const StatTile = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  transition: border-color 0.18s ease, transform 0.18s ease;
  &:hover {
    border-color: ${({ theme }) => theme.palette.borderColor.dark};
    transform: translateY(-1px);
  }
`;

export const StatValue = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme, $accent }) =>
    $accent || theme.palette.text.primary};
  font-variant-numeric: tabular-nums;
`;

export const StatLabel = styled(Typography)`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const KvRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-block: 6px;
  border-bottom: 1px dashed ${({ theme }) => theme.palette.borderColor.default};
  &:last-of-type { border-bottom: none; }
`;

export const KvLabel = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const KvValue = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  font-variant-numeric: tabular-nums;
`;

export const ChipCloud = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

/* ---------- pagination footer ---------- */

export const PaginationBar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  margin-block-start: 12px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;

export const PaginationInfo = styled(Typography)`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-variant-numeric: tabular-nums;
`;

export const PaginationControls = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/* ---------- coverage selector ---------- */

export const SectionHeader = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-block: 4px 10px;
`;

export const SectionLabel = styled(Typography)`
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const SectionActions = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 14px;
`;

export const SectionLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
  letter-spacing: 0.02em;
  transition: color 0.15s ease;
  &:hover { color: ${({ theme }) => theme.palette.text.primary}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

export const SelectableGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 10px;
`;

export const SelectableCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 10px;
  border-radius: 12px;
  cursor: pointer;
  font: inherit;
  text-align: center;
  user-select: none;
  background-color: ${({ theme, $selected }) =>
    $selected
      ? theme.palette.mode === "dark"
        ? "rgba(25, 118, 210, 0.22)"
        : "rgba(25, 118, 210, 0.10)"
      : theme.palette.background.default};
  color: ${({ theme, $selected }) =>
    $selected ? theme.palette.primary.main : theme.palette.text.primary};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.primary.main : theme.palette.borderColor.default};
  box-shadow: ${({ $selected, theme }) =>
    $selected
      ? `0 0 0 1px ${theme.palette.primary.main} inset`
      : "none"};
  transition: transform 0.18s ease, border-color 0.18s ease,
    background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ theme, $selected }) =>
      $selected ? theme.palette.primary.main : theme.palette.borderColor.dark};
    box-shadow: ${({ theme, $selected }) =>
      $selected
        ? `0 4px 14px rgba(25, 118, 210, 0.18), 0 0 0 1px ${theme.palette.primary.main} inset`
        : `0 4px 14px ${
            theme.palette.mode === "dark"
              ? "rgba(0, 0, 0, 0.4)"
              : "rgba(0, 0, 0, 0.08)"
          }`};
  }
`;

export const SelectableCardCheck = styled(Box)`
  position: absolute;
  top: 8px;
  inset-inline-end: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.palette.primary.main : "transparent"};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.primary.main : "transparent"};
  transition: all 0.18s ease;
  & svg { width: 13px; height: 13px; }
`;

export const CountryFlag = styled.span`
  font-size: 1.85rem;
  line-height: 1;
  filter: ${({ $selected }) => ($selected ? "none" : "saturate(0.95)")};
`;

export const FlagImg = styled.img`
  width: 44px;
  height: 30px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
`;

export const FlagFallback = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 30px;
  font-size: 1.5rem;
  line-height: 1;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.background.secondary};
`;

export const CardName = styled(Typography)`
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.25;
`;

export const TopicSwatch = styled(Box)`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.04em;
`;

export const SaveBar = styled(Box)`
  position: sticky;
  bottom: 0;
  margin-block-start: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  border-radius: 14px;
  background-color: ${({ theme }) => theme.palette.background.default};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.dark};
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  z-index: 1;
`;

export const SaveBarSummary = styled(Typography)`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const SaveBarActions = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

/* ---------- browse-countries modal ---------- */

export const BrowseTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  color: ${({ theme }) => theme.palette.text.primary};
  border: 1px dashed ${({ theme }) => theme.palette.borderColor.dark};
  transition: border-color 0.18s ease, background-color 0.18s ease,
    transform 0.18s ease;
  &:hover {
    border-color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.default};
    transform: translateY(-1px);
  }
`;

export const InlineEmpty = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-radius: 12px;
  border: 1px dashed ${({ theme }) => theme.palette.borderColor.default};
  background-color: ${({ theme }) => theme.palette.background.secondary};
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.8125rem;
  font-weight: 500;
`;

export const ModalHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px 8px;
`;

export const ModalTitle = styled(Typography)`
  font-size: 1.0625rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const ModalToolbar = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;

export const ModalSearch = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 14px;
  font: inherit;
  font-size: 0.875rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  background-color: ${({ theme }) => theme.palette.background.secondary};
  color: ${({ theme }) => theme.palette.text.primary};
  outline: none;
  transition: border-color 0.18s ease, background-color 0.18s ease;
  &::placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }
  &:focus {
    border-color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.default};
  }
`;

export const ModalBody = styled(Box)`
  padding: 16px 20px 20px;
  max-height: 60vh;
  overflow-y: auto;
`;

export const ModalFooter = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px 16px;
  border-top: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;

export const ModalCount = styled(Typography)`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const ModalEmpty = styled(Box)`
  text-align: center;
  padding: 40px 16px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.875rem;
`;

/* ---------- topic strip ---------- */

/* Sits directly under the page tabs as the feed's primary navigation, above
   the toolbar that refines it. Chrome, not content — it follows the UI
   direction, never the content RTL switch. */
export const TopicStrip = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  /* Room inside the scroll box for the hover lift and the focus ring. */
  padding: 4px 2px 6px;
  margin-block: 14px 12px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

/* Groups the refinements into one surface so they read as a band rather than
   four loose controls — and bookends the grid with PaginationBar, which wears
   the same recessed treatment. */
export const Toolbar = styled(Box)`
  padding: 10px 12px;
  margin-block-end: 14px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.palette.background.secondary};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;

/* A filter, not a tab — deliberately unlike the page's underline tabs above it,
   so two strips in a column don't read as two levels of the same control. */
export const TopicPill = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  white-space: nowrap;
  transition: background-color 0.16s ease, border-color 0.16s ease,
    color 0.16s ease, transform 0.16s ease;
  background-color: ${({ theme, $active, $bg }) =>
    $active ? $bg : theme.palette.background.secondary};
  border: 1px solid
    ${({ theme, $active, $fg }) =>
      $active ? $fg : theme.palette.borderColor.default};
  color: ${({ theme, $active, $fg }) =>
    $active ? $fg : theme.palette.text.secondary};

  &:hover {
    transform: translateY(-1px);
    color: ${({ theme, $active, $fg }) =>
      $active ? $fg : theme.palette.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.text.primary};
    outline-offset: 2px;
  }
`;

/* Separates the single-select topic pills from the written toggle, which is an
   independent filter rather than another desk. */
export const StripDivider = styled.span`
  flex-shrink: 0;
  align-self: stretch;
  width: 1px;
  margin-inline: 4px;
  background-color: ${({ theme }) => theme.palette.borderColor.dark};
`;

export const PillCount = styled.span`
  min-width: 18px;
  padding-inline: 5px;
  border-radius: 999px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background-color: ${({ theme }) => theme.palette.background.dark};
`;

/* ---------- story cards ---------- */

export const CardGrid = styled(Box)`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  align-content: start;
  /* Load-bearing. The cards set overflow:hidden, which drops a grid item's
     automatic minimum size to 0 — so in a height-constrained grid the rows
     collapse under their own content instead of overflowing, and every card
     squashes down to its meta line. Pinning rows to max-content makes the
     grid scroll like it's meant to. (One row's worth of cards fits, which is
     why a single-card page looked fine.) */
  grid-auto-rows: max-content;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 2px 8px;
`;

const cardEnter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`;

/* Holds, then fades — a deep-linked card should announce itself and then stop
   looking selected, so nobody has to dismiss it. */
const ringFade = keyframes`
  0%, 62% { opacity: 1; }
  100%    { opacity: 0; }
`;

export const StoryCard = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 14px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: box-shadow 0.16s ease, border-color 0.16s ease,
    transform 0.16s ease;
  animation: ${cardEnter} 0.26s ease-out backwards;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(16, 24, 40, 0.1);
    border-color: ${({ theme }) => theme.palette.borderColor.dark};
  }

  /* The ring lives on a pseudo-element, inset: the card clips its overflow (for
     the progress bar), so an outer ring would be shaved off. Keeping it off the
     card itself also leaves the entry animation and hover transform alone. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    opacity: 0;
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.palette.primary.main};
    animation: ${({ $flash }) => ($flash ? ringFade : "none")} 2.4s ease-out
      forwards;
  }
`;

export const CardMeta = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const HeatBadge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 7px;
  font-size: 0.75rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.6;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  direction: ltr;
  unicode-bidi: isolate;
  user-select: none;
`;

export const MetaDot = styled.span`
  opacity: 0.4;
  user-select: none;
`;

export const CardTitle = styled(Typography)`
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.primary};
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CardDesc = styled(Typography)`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.text.secondary};
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1 1 auto;
`;

export const CardFoot = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  /* Pins every Generate button to the same line across a grid row. */
  margin-block-start: auto;
  padding-block-start: 2px;
`;

/* The source name is the link out — an extra icon button for the same job was
   one target too many on a card this small. */
export const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.secondary};
  /* Takes the slack and ellipsises: a long publisher name must never be able
     to squeeze the action beside it. */
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  direction: ltr;
  unicode-bidi: isolate;
  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: underline;
  }
`;

/* Every card footer's action side, whatever state it's in — one wrapper so the
   button keeps its natural width across all of them and never wraps. */
export const CardAction = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  .MuiButton-root {
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

export const StatePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 7px;
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
  background-color: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`;

export const CardSkeleton = styled(ShimmerBar)`
  height: 178px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor.default};
`;
